import { Tool } from '@modelcontextprotocol/sdk/types.js';
import Handlebars from 'handlebars';
import {
  ScaffoldTestsInputSchema,
  type Config,
  type ScaffoldResult,
} from '../types/index.js';
import { writeText, ensureDirectory, validatePathSecurity } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const scaffoldTestsTool: Tool = {
  name: 'scaffold_tests',
  description: 'Generate unit, integration, and security tests for SmartStack entities, services, controllers, validators, and repositories. Ensures non-regression and maximum security coverage.',
  inputSchema: {
    type: 'object',
    properties: {
      target: {
        type: 'string',
        enum: ['entity', 'service', 'controller', 'validator', 'repository', 'all'],
        description: 'Type of component to test',
      },
      name: {
        type: 'string',
        description: 'Component name (PascalCase, e.g., "User", "Order")',
      },
      testTypes: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['unit', 'integration', 'security', 'e2e'],
        },
        default: ['unit'],
        description: 'Types of tests to generate',
      },
      options: {
        type: 'object',
        properties: {
          includeEdgeCases: {
            type: 'boolean',
            default: true,
            description: 'Include edge case tests',
          },
          includeTenantIsolation: {
            type: 'boolean',
            default: true,
            description: 'Include tenant isolation tests',
          },
          includeSoftDelete: {
            type: 'boolean',
            default: true,
            description: 'Include soft delete tests',
          },
          includeAudit: {
            type: 'boolean',
            default: true,
            description: 'Include audit trail tests',
          },
          includeValidation: {
            type: 'boolean',
            default: true,
            description: 'Include validation tests',
          },
          includeAuthorization: {
            type: 'boolean',
            default: false,
            description: 'Include authorization tests',
          },
          includePerformance: {
            type: 'boolean',
            default: false,
            description: 'Include performance tests',
          },
          isSystemEntity: {
            type: 'boolean',
            default: false,
            description: 'If true, entity has no TenantId',
          },
          dryRun: {
            type: 'boolean',
            default: false,
            description: 'Preview without writing files',
          },
        },
      },
    },
    required: ['target', 'name'],
  },
};

// Register Handlebars helpers for tests
Handlebars.registerHelper('pascalCase', (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
});

Handlebars.registerHelper('camelCase', (str: string) => {
  if (!str) return '';
  return str.charAt(0).toLowerCase() + str.slice(1);
});

Handlebars.registerHelper('unless', function(this: unknown, conditional: boolean, options: Handlebars.HelperOptions) {
  if (!conditional) {
    return options.fn(this);
  }
  return options.inverse(this);
});

// ============================================================================
// Test Templates
// ============================================================================

const entityTestTemplate = `using System;
using FluentAssertions;
using Xunit;
using {{domainNamespace}};

namespace {{testNamespace}}.Unit.Domain;

/// <summary>
/// Unit tests for {{name}} entity
/// Tests: Factory methods, Soft delete, Audit trail, Validation
/// </summary>
public class {{name}}Tests
{
    private const string ValidCode = "test_code";
{{#unless isSystemEntity}}
    private readonly Guid _tenantId = Guid.NewGuid();
{{/unless}}

    #region Factory Method Tests

    [Fact]
    public void Create_WhenValidData_ShouldCreateEntity()
    {
        // Arrange
        var code = ValidCode;
{{#unless isSystemEntity}}
        var tenantId = _tenantId;
{{/unless}}

        // Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}tenantId, {{/unless}}code);

        // Assert
        entity.Should().NotBeNull();
        entity.Id.Should().NotBeEmpty();
        entity.Code.Should().Be(code.ToLowerInvariant());
{{#unless isSystemEntity}}
        entity.TenantId.Should().Be(tenantId);
{{/unless}}
        entity.CreatedAt.Should().BeCloseTo(DateTime.UtcNow, TimeSpan.FromSeconds(1));
        entity.IsDeleted.Should().BeFalse();
    }

{{#unless isSystemEntity}}
    [Fact]
    public void Create_WhenEmptyTenantId_ShouldThrow()
    {
        // Arrange
        var tenantId = Guid.Empty;
        var code = ValidCode;

        // Act
        var act = () => {{name}}.Create(tenantId, code);

        // Assert
        act.Should().Throw<ArgumentException>()
           .WithParameterName("tenantId");
    }
{{/unless}}

    [Theory]
    [InlineData("")]
    [InlineData("   ")]
    public void Create_WhenEmptyCode_ShouldHandleGracefully(string code)
    {
        // Arrange
{{#unless isSystemEntity}}
        var tenantId = _tenantId;
{{/unless}}

        // Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}tenantId, {{/unless}}code);

        // Assert
        entity.Should().NotBeNull();
        entity.Code.Should().BeEmpty();
    }

    #endregion

{{#if includeSoftDelete}}
    #region Soft Delete Tests

    [Fact]
    public void SoftDelete_WhenCalled_ShouldSetIsDeletedTrue()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode);
        var deletedBy = "test_user";

        // Act
        entity.SoftDelete(deletedBy);

        // Assert
        entity.IsDeleted.Should().BeTrue();
        entity.DeletedAt.Should().NotBeNull();
        entity.DeletedAt.Should().BeCloseTo(DateTime.UtcNow, TimeSpan.FromSeconds(1));
        entity.DeletedBy.Should().Be(deletedBy);
    }

    [Fact]
    public void SoftDelete_WhenAlreadyDeleted_ShouldNotChangeDeletedAt()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode);
        entity.SoftDelete("first_deleter");
        var originalDeletedAt = entity.DeletedAt;

        // Act
        entity.SoftDelete("second_deleter");

        // Assert
        entity.DeletedAt.Should().Be(originalDeletedAt);
    }

    [Fact]
    public void Restore_WhenSoftDeleted_ShouldClearDeletedFields()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode);
        entity.SoftDelete("deleter");
        var restoredBy = "restorer";

        // Act
        entity.Restore(restoredBy);

        // Assert
        entity.IsDeleted.Should().BeFalse();
        entity.DeletedAt.Should().BeNull();
        entity.DeletedBy.Should().BeNull();
        entity.UpdatedBy.Should().Be(restoredBy);
    }

    #endregion
{{/if}}

{{#if includeAudit}}
    #region Audit Trail Tests

    [Fact]
    public void Create_ShouldSetCreatedAtToUtcNow()
    {
        // Arrange & Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode);

        // Assert
        entity.CreatedAt.Should().BeCloseTo(DateTime.UtcNow, TimeSpan.FromSeconds(1));
        entity.CreatedAt.Kind.Should().Be(DateTimeKind.Utc);
    }

    [Fact]
    public void Create_WhenCreatedByProvided_ShouldSetCreatedBy()
    {
        // Arrange
        var createdBy = "test_creator";

        // Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode, createdBy);

        // Assert
        entity.CreatedBy.Should().Be(createdBy);
    }

    [Fact]
    public void Update_WhenCalled_ShouldUpdateAuditFields()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}ValidCode);
        var originalCreatedAt = entity.CreatedAt;
        var updatedBy = "updater";

        // Act
        entity.Update(updatedBy);

        // Assert
        entity.UpdatedAt.Should().NotBeNull();
        entity.UpdatedAt.Should().BeCloseTo(DateTime.UtcNow, TimeSpan.FromSeconds(1));
        entity.UpdatedBy.Should().Be(updatedBy);
        entity.CreatedAt.Should().Be(originalCreatedAt);
    }

    #endregion
{{/if}}

{{#if includeEdgeCases}}
    #region Edge Cases

    [Fact]
    public void Create_WithSpecialCharactersInCode_ShouldNormalize()
    {
        // Arrange
        var code = "Test-Code_123";

        // Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}code);

        // Assert
        entity.Code.Should().Be(code.ToLowerInvariant());
    }

    [Fact]
    public void Create_WithMaxLengthCode_ShouldSucceed()
    {
        // Arrange
        var code = new string('a', 100);

        // Act
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}code);

        // Assert
        entity.Code.Should().HaveLength(100);
    }

    #endregion
{{/if}}
}
`;

const serviceTestTemplate = `using System;
using System.Collections.Generic;
using System.Threading;
using System.Threading.Tasks;
using FluentAssertions;
using Microsoft.Extensions.Logging;
using Moq;
using Xunit;
using {{applicationNamespace}}.Services;
using {{applicationNamespace}}.Repositories;
using {{domainNamespace}};

namespace {{testNamespace}}.Unit.Services;

/// <summary>
/// Unit tests for {{name}}Service
/// Tests: CRUD operations, Business logic, Error handling, Tenant isolation
/// </summary>
public class {{name}}ServiceTests
{
    private readonly Mock<I{{name}}Repository> _repositoryMock;
    private readonly Mock<ILogger<{{name}}Service>> _loggerMock;
    private readonly {{name}}Service _sut;
{{#unless isSystemEntity}}
    private readonly Guid _tenantId = Guid.NewGuid();
{{/unless}}

    public {{name}}ServiceTests()
    {
        _repositoryMock = new Mock<I{{name}}Repository>();
        _loggerMock = new Mock<ILogger<{{name}}Service>>();
        _sut = new {{name}}Service(
            _repositoryMock.Object,
            _loggerMock.Object
        );
    }

    #region GetByIdAsync Tests

    [Fact]
    public async Task GetByIdAsync_WhenEntityExists_ShouldReturnEntity()
    {
        // Arrange
        var id = Guid.NewGuid();
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");
        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ReturnsAsync(entity);

        // Act
        var result = await _sut.GetByIdAsync(id);

        // Assert
        result.Should().NotBeNull();
        _repositoryMock.Verify(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()), Times.Once);
    }

    [Fact]
    public async Task GetByIdAsync_WhenNotExists_ShouldReturnNull()
    {
        // Arrange
        var id = Guid.NewGuid();
        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var result = await _sut.GetByIdAsync(id);

        // Assert
        result.Should().BeNull();
    }

    [Fact]
    public async Task GetByIdAsync_WhenCancelled_ShouldThrowOperationCancelledException()
    {
        // Arrange
        var id = Guid.NewGuid();
        var cts = new CancellationTokenSource();
        cts.Cancel();

        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ThrowsAsync(new OperationCanceledException());

        // Act
        var act = async () => await _sut.GetByIdAsync(id, cts.Token);

        // Assert
        await act.Should().ThrowAsync<OperationCanceledException>();
    }

    #endregion

{{#unless isSystemEntity}}
    #region Tenant Isolation Tests

    [Fact]
    public async Task GetAllAsync_ShouldFilterByTenant()
    {
        // Arrange
        var entities = new List<{{name}}>
        {
            {{name}}.Create(_tenantId, "entity1"),
            {{name}}.Create(_tenantId, "entity2"),
        };
        _repositoryMock
            .Setup(r => r.GetAllByTenantAsync(_tenantId, It.IsAny<CancellationToken>()))
            .ReturnsAsync(entities);

        // Act
        var result = await _sut.GetAllAsync(_tenantId);

        // Assert
        result.Should().HaveCount(2);
        result.Should().OnlyContain(e => e.TenantId == _tenantId);
    }

    [Fact]
    public async Task CreateAsync_ShouldSetTenantId()
    {
        // Arrange
        {{name}}? capturedEntity = null;
        _repositoryMock
            .Setup(r => r.AddAsync(It.IsAny<{{name}}>(), It.IsAny<CancellationToken>()))
            .Callback<{{name}}, CancellationToken>((e, _) => capturedEntity = e)
            .ReturnsAsync(({{name}} e, CancellationToken _) => e);

        // Act
        await _sut.CreateAsync(_tenantId, "test_code");

        // Assert
        capturedEntity.Should().NotBeNull();
        capturedEntity!.TenantId.Should().Be(_tenantId);
    }

    [Fact]
    public async Task GetByIdAsync_WhenEntityBelongsToDifferentTenant_ShouldReturnNull()
    {
        // Arrange
        var id = Guid.NewGuid();
        var otherTenantId = Guid.NewGuid();
        var entity = {{name}}.Create(otherTenantId, "test");

        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ReturnsAsync(({{name}}?)null); // Repository should filter by tenant

        // Act
        var result = await _sut.GetByIdAsync(id, _tenantId);

        // Assert
        result.Should().BeNull();
    }

    #endregion
{{/unless}}

    #region Error Handling Tests

    [Fact]
    public async Task UpdateAsync_WhenEntityNotFound_ShouldThrow()
    {
        // Arrange
        var id = Guid.NewGuid();
        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var act = async () => await _sut.UpdateAsync(id, "new_code");

        // Assert
        await act.Should().ThrowAsync<InvalidOperationException>()
            .WithMessage("*not found*");
    }

    [Fact]
    public async Task DeleteAsync_WhenEntityNotFound_ShouldReturnFalse()
    {
        // Arrange
        var id = Guid.NewGuid();
        _repositoryMock
            .Setup(r => r.GetByIdAsync(id, It.IsAny<CancellationToken>()))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var result = await _sut.DeleteAsync(id);

        // Assert
        result.Should().BeFalse();
    }

    #endregion

{{#if includeEdgeCases}}
    #region Edge Cases

    [Fact]
    public async Task CreateAsync_WithEmptyCode_ShouldStillCreate()
    {
        // Arrange
        _repositoryMock
            .Setup(r => r.AddAsync(It.IsAny<{{name}}>(), It.IsAny<CancellationToken>()))
            .ReturnsAsync(({{name}} e, CancellationToken _) => e);

        // Act
        var result = await _sut.CreateAsync({{#unless isSystemEntity}}_tenantId, {{/unless}}"");

        // Assert
        result.Should().NotBeNull();
        result.Code.Should().BeEmpty();
    }

    [Fact]
    public async Task GetAllAsync_WhenNoEntities_ShouldReturnEmptyList()
    {
        // Arrange
        _repositoryMock
            .Setup(r => r.GetAllAsync(It.IsAny<CancellationToken>()))
            .ReturnsAsync(new List<{{name}}>());

        // Act
        var result = await _sut.GetAllAsync();

        // Assert
        result.Should().BeEmpty();
    }

    #endregion
{{/if}}
}
`;

const controllerTestTemplate = `using System;
using System.Collections.Generic;
using System.Net;
using System.Net.Http;
using System.Net.Http.Json;
using System.Threading.Tasks;
using FluentAssertions;
using Microsoft.AspNetCore.Mvc.Testing;
using Microsoft.Extensions.DependencyInjection;
using Moq;
using Xunit;
using {{apiNamespace}};
using {{applicationNamespace}}.Services;
using {{domainNamespace}};

namespace {{testNamespace}}.Integration.Controllers;

/// <summary>
/// Integration tests for {{name}}Controller
/// Tests: HTTP status codes, Authorization, Validation, CRUD operations
/// </summary>
public class {{name}}ControllerTests : IClassFixture<WebApplicationFactory<Program>>
{
    private readonly WebApplicationFactory<Program> _factory;
    private readonly HttpClient _client;
    private readonly Mock<I{{name}}Service> _serviceMock;
{{#unless isSystemEntity}}
    private readonly Guid _tenantId = Guid.NewGuid();
{{/unless}}

    public {{name}}ControllerTests(WebApplicationFactory<Program> factory)
    {
        _serviceMock = new Mock<I{{name}}Service>();

        _factory = factory.WithWebHostBuilder(builder =>
        {
            builder.ConfigureServices(services =>
            {
                // Replace the real service with our mock
                var descriptor = services.SingleOrDefault(
                    d => d.ServiceType == typeof(I{{name}}Service));

                if (descriptor != null)
                {
                    services.Remove(descriptor);
                }

                services.AddScoped(_ => _serviceMock.Object);
            });
        });

        _client = _factory.CreateClient();
{{#unless isSystemEntity}}
        _client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}
    }

    #region GET Tests

    [Fact]
    public async Task GetAll_WhenAuthorized_ShouldReturn200()
    {
        // Arrange
        var entities = new List<{{name}}>
        {
            {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"entity1"),
            {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"entity2"),
        };
        _serviceMock
            .Setup(s => s.GetAllAsync({{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(entities);

        // Act
        var response = await _client.GetAsync("/api/{{nameLower}}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.OK);
    }

    [Fact]
    public async Task GetById_WhenEntityExists_ShouldReturn200()
    {
        // Arrange
        var id = Guid.NewGuid();
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");
        _serviceMock
            .Setup(s => s.GetByIdAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(entity);

        // Act
        var response = await _client.GetAsync(\$"/api/{{nameLower}}/{id}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.OK);
    }

    [Fact]
    public async Task GetById_WhenNotFound_ShouldReturn404()
    {
        // Arrange
        var id = Guid.NewGuid();
        _serviceMock
            .Setup(s => s.GetByIdAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var response = await _client.GetAsync(\$"/api/{{nameLower}}/{id}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.NotFound);
    }

    #endregion

    #region POST Tests

    [Fact]
    public async Task Create_WhenValidData_ShouldReturn201()
    {
        // Arrange
        var request = new { Code = "new_entity" };
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}request.Code);
        _serviceMock
            .Setup(s => s.CreateAsync({{#unless isSystemEntity}}_tenantId, {{/unless}}request.Code, default))
            .ReturnsAsync(entity);

        // Act
        var response = await _client.PostAsJsonAsync("/api/{{nameLower}}", request);

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.Created);
    }

{{#if includeValidation}}
    [Fact]
    public async Task Create_WhenInvalidData_ShouldReturn400()
    {
        // Arrange
        var request = new { Code = (string?)null };

        // Act
        var response = await _client.PostAsJsonAsync("/api/{{nameLower}}", request);

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.BadRequest);
    }
{{/if}}

    #endregion

    #region PUT Tests

    [Fact]
    public async Task Update_WhenEntityExists_ShouldReturn200()
    {
        // Arrange
        var id = Guid.NewGuid();
        var request = new { Code = "updated_code" };
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");
        _serviceMock
            .Setup(s => s.GetByIdAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(entity);
        _serviceMock
            .Setup(s => s.UpdateAsync(id, request.Code, default))
            .ReturnsAsync(entity);

        // Act
        var response = await _client.PutAsJsonAsync(\$"/api/{{nameLower}}/{id}", request);

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.OK);
    }

    [Fact]
    public async Task Update_WhenNotFound_ShouldReturn404()
    {
        // Arrange
        var id = Guid.NewGuid();
        var request = new { Code = "updated_code" };
        _serviceMock
            .Setup(s => s.GetByIdAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var response = await _client.PutAsJsonAsync(\$"/api/{{nameLower}}/{id}", request);

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.NotFound);
    }

    #endregion

    #region DELETE Tests

    [Fact]
    public async Task Delete_WhenEntityExists_ShouldReturn204()
    {
        // Arrange
        var id = Guid.NewGuid();
        _serviceMock
            .Setup(s => s.DeleteAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(true);

        // Act
        var response = await _client.DeleteAsync(\$"/api/{{nameLower}}/{id}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.NoContent);
    }

    [Fact]
    public async Task Delete_WhenNotFound_ShouldReturn404()
    {
        // Arrange
        var id = Guid.NewGuid();
        _serviceMock
            .Setup(s => s.DeleteAsync(id, {{#unless isSystemEntity}}_tenantId, {{/unless}}default))
            .ReturnsAsync(false);

        // Act
        var response = await _client.DeleteAsync(\$"/api/{{nameLower}}/{id}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.NotFound);
    }

    #endregion

{{#if includeAuthorization}}
    #region Authorization Tests

    [Fact]
    public async Task GetAll_WhenUnauthorized_ShouldReturn401()
    {
        // Arrange
        var client = _factory.CreateClient();
        // Don't add auth header

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.Unauthorized);
    }

{{#unless isSystemEntity}}
    [Fact]
    public async Task GetById_WhenDifferentTenant_ShouldReturn403()
    {
        // Arrange
        var id = Guid.NewGuid();
        var client = _factory.CreateClient();
        client.DefaultRequestHeaders.Add("X-Tenant-Id", Guid.NewGuid().ToString());

        _serviceMock
            .Setup(s => s.GetByIdAsync(id, It.IsAny<Guid>(), default))
            .ReturnsAsync(({{name}}?)null);

        // Act
        var response = await client.GetAsync(\$"/api/{{nameLower}}/{id}");

        // Assert
        response.StatusCode.Should().BeOneOf(HttpStatusCode.Forbidden, HttpStatusCode.NotFound);
    }
{{/unless}}

    #endregion
{{/if}}
}
`;

const validatorTestTemplate = `using FluentAssertions;
using FluentValidation.TestHelper;
using Xunit;
using {{applicationNamespace}}.DTOs;
using {{applicationNamespace}}.Validators;

namespace {{testNamespace}}.Unit.Validators;

/// <summary>
/// Unit tests for {{name}} validators
/// Tests: Validation rules, Error messages
/// </summary>
public class {{name}}ValidatorTests
{
    private readonly Create{{name}}DtoValidator _createValidator;
    private readonly Update{{name}}DtoValidator _updateValidator;

    public {{name}}ValidatorTests()
    {
        _createValidator = new Create{{name}}DtoValidator();
        _updateValidator = new Update{{name}}DtoValidator();
    }

    #region Create Validator Tests

    [Fact]
    public void CreateValidator_WhenCodeIsEmpty_ShouldHaveError()
    {
        // Arrange
        var dto = new Create{{name}}Dto { Code = string.Empty };

        // Act
        var result = _createValidator.TestValidate(dto);

        // Assert
        result.ShouldHaveValidationErrorFor(x => x.Code)
              .WithErrorMessage("*required*");
    }

    [Fact]
    public void CreateValidator_WhenCodeIsTooLong_ShouldHaveError()
    {
        // Arrange
        var dto = new Create{{name}}Dto { Code = new string('a', 101) };

        // Act
        var result = _createValidator.TestValidate(dto);

        // Assert
        result.ShouldHaveValidationErrorFor(x => x.Code)
              .WithErrorMessage("*100 characters*");
    }

    [Fact]
    public void CreateValidator_WhenValidData_ShouldNotHaveErrors()
    {
        // Arrange
        var dto = new Create{{name}}Dto { Code = "valid_code" };

        // Act
        var result = _createValidator.TestValidate(dto);

        // Assert
        result.ShouldNotHaveAnyValidationErrors();
    }

    #endregion

    #region Update Validator Tests

    [Fact]
    public void UpdateValidator_WhenCodeIsEmpty_ShouldHaveError()
    {
        // Arrange
        var dto = new Update{{name}}Dto { Code = string.Empty };

        // Act
        var result = _updateValidator.TestValidate(dto);

        // Assert
        result.ShouldHaveValidationErrorFor(x => x.Code);
    }

    [Fact]
    public void UpdateValidator_WhenValidData_ShouldNotHaveErrors()
    {
        // Arrange
        var dto = new Update{{name}}Dto { Code = "updated_code" };

        // Act
        var result = _updateValidator.TestValidate(dto);

        // Assert
        result.ShouldNotHaveAnyValidationErrors();
    }

    #endregion

{{#if includeEdgeCases}}
    #region Edge Cases

    [Theory]
    [InlineData("test-code")]
    [InlineData("test_code")]
    [InlineData("test.code")]
    public void CreateValidator_WhenCodeHasSpecialCharacters_ShouldPass(string code)
    {
        // Arrange
        var dto = new Create{{name}}Dto { Code = code };

        // Act
        var result = _createValidator.TestValidate(dto);

        // Assert
        result.ShouldNotHaveValidationErrorFor(x => x.Code);
    }

    [Theory]
    [InlineData("   leading")]
    [InlineData("trailing   ")]
    [InlineData("   both   ")]
    public void CreateValidator_WhenCodeHasWhitespace_ShouldTrimAndValidate(string code)
    {
        // Arrange
        var dto = new Create{{name}}Dto { Code = code };

        // Act
        var result = _createValidator.TestValidate(dto);

        // Assert
        // Depending on implementation, may pass or fail
        // This test documents the expected behavior
    }

    #endregion
{{/if}}
}
`;

const repositoryTestTemplate = `using System;
using System.Collections.Generic;
using System.Linq;
using System.Threading.Tasks;
using FluentAssertions;
using Microsoft.EntityFrameworkCore;
using Xunit;
using {{infrastructureNamespace}}.Persistence;
using {{infrastructureNamespace}}.Repositories;
using {{domainNamespace}};

namespace {{testNamespace}}.Integration.Repositories;

/// <summary>
/// Integration tests for {{name}}Repository
/// Tests: CRUD with DbContext, Queries, Tenant scope
/// </summary>
public class {{name}}RepositoryTests : IDisposable
{
    private readonly ApplicationDbContext _context;
    private readonly {{name}}Repository _repository;
{{#unless isSystemEntity}}
    private readonly Guid _tenantId = Guid.NewGuid();
{{/unless}}

    public {{name}}RepositoryTests()
    {
        var options = new DbContextOptionsBuilder<ApplicationDbContext>()
            .UseInMemoryDatabase(databaseName: Guid.NewGuid().ToString())
            .Options;

        _context = new ApplicationDbContext(options);
        _repository = new {{name}}Repository(_context);
    }

    public void Dispose()
    {
        _context.Dispose();
    }

    #region GetByIdAsync Tests

    [Fact]
    public async Task GetByIdAsync_WhenEntityExists_ShouldReturnEntity()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");
        await _context.Set<{{name}}>().AddAsync(entity);
        await _context.SaveChangesAsync();

        // Act
        var result = await _repository.GetByIdAsync(entity.Id);

        // Assert
        result.Should().NotBeNull();
        result!.Id.Should().Be(entity.Id);
    }

    [Fact]
    public async Task GetByIdAsync_WhenNotExists_ShouldReturnNull()
    {
        // Arrange
        var id = Guid.NewGuid();

        // Act
        var result = await _repository.GetByIdAsync(id);

        // Assert
        result.Should().BeNull();
    }

    #endregion

    #region AddAsync Tests

    [Fact]
    public async Task AddAsync_ShouldPersistEntity()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");

        // Act
        var result = await _repository.AddAsync(entity);
        await _context.SaveChangesAsync();

        // Assert
        result.Should().NotBeNull();
        var persisted = await _context.Set<{{name}}>().FindAsync(entity.Id);
        persisted.Should().NotBeNull();
    }

    #endregion

    #region UpdateAsync Tests

    [Fact]
    public async Task UpdateAsync_ShouldUpdateEntity()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"original");
        await _context.Set<{{name}}>().AddAsync(entity);
        await _context.SaveChangesAsync();

        // Act
        entity.Update("updater");
        await _repository.UpdateAsync(entity);
        await _context.SaveChangesAsync();

        // Assert
        var updated = await _context.Set<{{name}}>().FindAsync(entity.Id);
        updated!.UpdatedBy.Should().Be("updater");
    }

    #endregion

    #region DeleteAsync Tests

    [Fact]
    public async Task DeleteAsync_ShouldRemoveEntity()
    {
        // Arrange
        var entity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"test");
        await _context.Set<{{name}}>().AddAsync(entity);
        await _context.SaveChangesAsync();

        // Act
        await _repository.DeleteAsync(entity);
        await _context.SaveChangesAsync();

        // Assert
        var deleted = await _context.Set<{{name}}>().FindAsync(entity.Id);
        deleted.Should().BeNull();
    }

    #endregion

{{#unless isSystemEntity}}
    #region Tenant Isolation Tests

    [Fact]
    public async Task GetAllByTenantAsync_ShouldOnlyReturnTenantEntities()
    {
        // Arrange
        var otherTenantId = Guid.NewGuid();
        await _context.Set<{{name}}>().AddRangeAsync(
            {{name}}.Create(_tenantId, "entity1"),
            {{name}}.Create(_tenantId, "entity2"),
            {{name}}.Create(otherTenantId, "other_tenant_entity")
        );
        await _context.SaveChangesAsync();

        // Act
        var result = await _repository.GetAllByTenantAsync(_tenantId);

        // Assert
        result.Should().HaveCount(2);
        result.Should().OnlyContain(e => e.TenantId == _tenantId);
    }

    [Fact]
    public async Task ExistsAsync_WhenEntityInDifferentTenant_ShouldReturnFalse()
    {
        // Arrange
        var otherTenantId = Guid.NewGuid();
        var entity = {{name}}.Create(otherTenantId, "test");
        await _context.Set<{{name}}>().AddAsync(entity);
        await _context.SaveChangesAsync();

        // Act
        var result = await _repository.ExistsAsync(entity.Id, _tenantId);

        // Assert
        result.Should().BeFalse();
    }

    #endregion
{{/unless}}

{{#if includeSoftDelete}}
    #region Soft Delete Tests

    [Fact]
    public async Task GetAllAsync_ShouldExcludeSoftDeletedEntities()
    {
        // Arrange
        var activeEntity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"active");
        var deletedEntity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"deleted");
        deletedEntity.SoftDelete("deleter");

        await _context.Set<{{name}}>().AddRangeAsync(activeEntity, deletedEntity);
        await _context.SaveChangesAsync();

        // Act
        var result = await _repository.GetAllAsync();

        // Assert
        result.Should().ContainSingle();
        result.First().Code.Should().Be("active");
    }

    [Fact]
    public async Task GetAllIncludingDeletedAsync_ShouldIncludeSoftDeletedEntities()
    {
        // Arrange
        var activeEntity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"active");
        var deletedEntity = {{name}}.Create({{#unless isSystemEntity}}_tenantId, {{/unless}}"deleted");
        deletedEntity.SoftDelete("deleter");

        await _context.Set<{{name}}>().AddRangeAsync(activeEntity, deletedEntity);
        await _context.SaveChangesAsync();

        // Act
        var result = await _repository.GetAllIncludingDeletedAsync();

        // Assert
        result.Should().HaveCount(2);
    }

    #endregion
{{/if}}
}
`;

const securityTestTemplate = `using System;
using System.Net;
using System.Net.Http;
using System.Threading.Tasks;
using FluentAssertions;
using Microsoft.AspNetCore.Mvc.Testing;
using Xunit;
using {{apiNamespace}};

namespace {{testNamespace}}.Security;

/// <summary>
/// Security tests for {{name}}
/// Tests: Tenant isolation, Authorization, Input validation, Injection prevention
/// </summary>
public class {{name}}SecurityTests : IClassFixture<WebApplicationFactory<Program>>
{
    private readonly WebApplicationFactory<Program> _factory;
{{#unless isSystemEntity}}
    private readonly Guid _tenantId = Guid.NewGuid();
{{/unless}}

    public {{name}}SecurityTests(WebApplicationFactory<Program> factory)
    {
        _factory = factory;
    }

{{#unless isSystemEntity}}
    #region Tenant Isolation Tests

    [Fact]
    public async Task Request_WithoutTenantHeader_ShouldReturn400()
    {
        // Arrange
        var client = _factory.CreateClient();
        // Intentionally not adding X-Tenant-Id header

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}");

        // Assert
        response.StatusCode.Should().BeOneOf(
            HttpStatusCode.BadRequest,
            HttpStatusCode.Unauthorized
        );
    }

    [Fact]
    public async Task Request_WithInvalidTenantId_ShouldReturn400()
    {
        // Arrange
        var client = _factory.CreateClient();
        client.DefaultRequestHeaders.Add("X-Tenant-Id", "invalid-guid");

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.BadRequest);
    }

    [Fact]
    public async Task Request_WithEmptyTenantId_ShouldReturn400()
    {
        // Arrange
        var client = _factory.CreateClient();
        client.DefaultRequestHeaders.Add("X-Tenant-Id", Guid.Empty.ToString());

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.BadRequest);
    }

    #endregion
{{/unless}}

    #region Input Validation Security Tests

    [Theory]
    [InlineData("<script>alert('xss')</script>")]
    [InlineData("'; DROP TABLE users; --")]
    [InlineData("{{'{{'}}constructor.prototype{{'}}'}}")]
    public async Task Create_WithMaliciousInput_ShouldSanitizeOrReject(string maliciousInput)
    {
        // Arrange
        var client = _factory.CreateClient();
{{#unless isSystemEntity}}
        client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}
        var request = new { Code = maliciousInput };

        // Act
        var response = await client.PostAsJsonAsync("/api/{{nameLower}}", request);

        // Assert
        // Should either reject (400) or sanitize (201 with cleaned data)
        response.StatusCode.Should().BeOneOf(
            HttpStatusCode.BadRequest,
            HttpStatusCode.Created,
            HttpStatusCode.UnprocessableEntity
        );
    }

    [Theory]
    [InlineData("../../../etc/passwd")]
    [InlineData("..\\\\..\\\\..\\\\windows\\\\system32")]
    [InlineData("....//....//....//etc/passwd")]
    public async Task Create_WithPathTraversalAttempt_ShouldReject(string pathTraversal)
    {
        // Arrange
        var client = _factory.CreateClient();
{{#unless isSystemEntity}}
        client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}
        var request = new { Code = pathTraversal };

        // Act
        var response = await client.PostAsJsonAsync("/api/{{nameLower}}", request);

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.BadRequest);
    }

    #endregion

    #region ID Manipulation Tests

    [Fact]
    public async Task GetById_WithNegativeId_ShouldReturn400Or404()
    {
        // Arrange
        var client = _factory.CreateClient();
{{#unless isSystemEntity}}
        client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}/00000000-0000-0000-0000-000000000000");

        // Assert
        response.StatusCode.Should().BeOneOf(
            HttpStatusCode.BadRequest,
            HttpStatusCode.NotFound
        );
    }

    [Fact]
    public async Task GetById_WithMalformedGuid_ShouldReturn400()
    {
        // Arrange
        var client = _factory.CreateClient();
{{#unless isSystemEntity}}
        client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}

        // Act
        var response = await client.GetAsync("/api/{{nameLower}}/not-a-guid");

        // Assert
        response.StatusCode.Should().Be(HttpStatusCode.BadRequest);
    }

    #endregion

    #region Rate Limiting Tests (if applicable)

    [Fact]
    public async Task MultipleRapidRequests_ShouldNotCauseDenialOfService()
    {
        // Arrange
        var client = _factory.CreateClient();
{{#unless isSystemEntity}}
        client.DefaultRequestHeaders.Add("X-Tenant-Id", _tenantId.ToString());
{{/unless}}
        var tasks = new List<Task<HttpResponseMessage>>();

        // Act
        for (int i = 0; i < 10; i++)
        {
            tasks.Add(client.GetAsync("/api/{{nameLower}}"));
        }
        var responses = await Task.WhenAll(tasks);

        // Assert
        // Should handle without crashing (may return 429 if rate limiting is enabled)
        responses.Should().OnlyContain(r =>
            r.StatusCode == HttpStatusCode.OK ||
            r.StatusCode == HttpStatusCode.TooManyRequests);
    }

    #endregion
}
`;

// ============================================================================
// Handler Functions
// ============================================================================

export async function handleScaffoldTests(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ScaffoldTestsInputSchema.parse(args);
  const dryRun = input.options?.dryRun || false;

  logger.info('Scaffolding tests', { target: input.target, name: input.name, dryRun });

  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  const result: ScaffoldResult = {
    success: true,
    files: [],
    instructions: [],
  };

  const options = {
    includeEdgeCases: input.options?.includeEdgeCases ?? true,
    includeTenantIsolation: input.options?.includeTenantIsolation ?? true,
    includeSoftDelete: input.options?.includeSoftDelete ?? true,
    includeAudit: input.options?.includeAudit ?? true,
    includeValidation: input.options?.includeValidation ?? true,
    includeAuthorization: input.options?.includeAuthorization ?? false,
    isSystemEntity: input.options?.isSystemEntity ?? false,
  };

  const testTypes = input.testTypes || ['unit'];

  try {
    switch (input.target) {
      case 'entity':
        await scaffoldEntityTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
      case 'service':
        await scaffoldServiceTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
      case 'controller':
        await scaffoldControllerTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
      case 'validator':
        await scaffoldValidatorTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
      case 'repository':
        await scaffoldRepositoryTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
      case 'all':
        await scaffoldEntityTests(input.name, options, testTypes, structure, config, result, dryRun);
        await scaffoldServiceTests(input.name, options, testTypes, structure, config, result, dryRun);
        await scaffoldControllerTests(input.name, options, testTypes, structure, config, result, dryRun);
        await scaffoldValidatorTests(input.name, options, testTypes, structure, config, result, dryRun);
        await scaffoldRepositoryTests(input.name, options, testTypes, structure, config, result, dryRun);
        break;
    }
  } catch (error) {
    result.success = false;
    result.instructions.push(`Error: ${error instanceof Error ? error.message : String(error)}`);
  }

  return formatTestResult(result, input.target, input.name, dryRun);
}

// ============================================================================
// Scaffold Functions
// ============================================================================

interface TestOptions {
  includeEdgeCases: boolean;
  includeTenantIsolation: boolean;
  includeSoftDelete: boolean;
  includeAudit: boolean;
  includeValidation: boolean;
  includeAuthorization: boolean;
  isSystemEntity: boolean;
}

async function scaffoldEntityTests(
  name: string,
  options: TestOptions,
  testTypes: string[],
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean
): Promise<void> {
  const testNamespace = `${config.conventions.namespaces.application}.Tests`;
  const domainNamespace = config.conventions.namespaces.domain;

  const context = {
    name,
    testNamespace,
    domainNamespace,
    ...options,
  };

  // Unit tests
  if (testTypes.includes('unit')) {
    const content = Handlebars.compile(entityTestTemplate)(context);
    const testPath = path.join(structure.root, 'Tests', 'Unit', 'Domain', `${name}Tests.cs`);

    validatePathSecurity(testPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(testPath));
      await writeText(testPath, content);
    }

    result.files.push({
      path: path.relative(structure.root, testPath),
      content,
      type: 'created',
    });
  }

  // Security tests
  if (testTypes.includes('security')) {
    const securityContent = Handlebars.compile(securityTestTemplate)({
      ...context,
      nameLower: name.charAt(0).toLowerCase() + name.slice(1),
      apiNamespace: config.conventions.namespaces.api,
    });
    const securityPath = path.join(structure.root, 'Tests', 'Security', `${name}SecurityTests.cs`);

    validatePathSecurity(securityPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(securityPath));
      await writeText(securityPath, securityContent);
    }

    result.files.push({
      path: path.relative(structure.root, securityPath),
      content: securityContent,
      type: 'created',
    });
  }

  result.instructions.push(`Add package reference: <PackageReference Include="FluentAssertions" Version="6.*" />`);
  result.instructions.push(`Add package reference: <PackageReference Include="xunit" Version="2.*" />`);
}

async function scaffoldServiceTests(
  name: string,
  options: TestOptions,
  testTypes: string[],
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean
): Promise<void> {
  const testNamespace = `${config.conventions.namespaces.application}.Tests`;
  const applicationNamespace = config.conventions.namespaces.application;
  const domainNamespace = config.conventions.namespaces.domain;

  const context = {
    name,
    testNamespace,
    applicationNamespace,
    domainNamespace,
    ...options,
  };

  if (testTypes.includes('unit')) {
    const content = Handlebars.compile(serviceTestTemplate)(context);
    const testPath = path.join(structure.root, 'Tests', 'Unit', 'Services', `${name}ServiceTests.cs`);

    validatePathSecurity(testPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(testPath));
      await writeText(testPath, content);
    }

    result.files.push({
      path: path.relative(structure.root, testPath),
      content,
      type: 'created',
    });
  }

  result.instructions.push(`Add package reference: <PackageReference Include="Moq" Version="4.*" />`);
}

async function scaffoldControllerTests(
  name: string,
  options: TestOptions,
  testTypes: string[],
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean
): Promise<void> {
  const testNamespace = `${config.conventions.namespaces.application}.Tests`;
  const applicationNamespace = config.conventions.namespaces.application;
  const domainNamespace = config.conventions.namespaces.domain;
  const apiNamespace = config.conventions.namespaces.api;

  const context = {
    name,
    nameLower: name.charAt(0).toLowerCase() + name.slice(1),
    testNamespace,
    applicationNamespace,
    domainNamespace,
    apiNamespace,
    ...options,
  };

  if (testTypes.includes('integration')) {
    const content = Handlebars.compile(controllerTestTemplate)(context);
    const testPath = path.join(structure.root, 'Tests', 'Integration', 'Controllers', `${name}ControllerTests.cs`);

    validatePathSecurity(testPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(testPath));
      await writeText(testPath, content);
    }

    result.files.push({
      path: path.relative(structure.root, testPath),
      content,
      type: 'created',
    });
  }

  if (testTypes.includes('security')) {
    const securityContent = Handlebars.compile(securityTestTemplate)(context);
    const securityPath = path.join(structure.root, 'Tests', 'Security', `${name}SecurityTests.cs`);

    validatePathSecurity(securityPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(securityPath));
      await writeText(securityPath, securityContent);
    }

    result.files.push({
      path: path.relative(structure.root, securityPath),
      content: securityContent,
      type: 'created',
    });
  }

  result.instructions.push(`Add package reference: <PackageReference Include="Microsoft.AspNetCore.Mvc.Testing" Version="8.*" />`);
}

async function scaffoldValidatorTests(
  name: string,
  options: TestOptions,
  testTypes: string[],
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean
): Promise<void> {
  const testNamespace = `${config.conventions.namespaces.application}.Tests`;
  const applicationNamespace = config.conventions.namespaces.application;

  const context = {
    name,
    testNamespace,
    applicationNamespace,
    ...options,
  };

  if (testTypes.includes('unit')) {
    const content = Handlebars.compile(validatorTestTemplate)(context);
    const testPath = path.join(structure.root, 'Tests', 'Unit', 'Validators', `${name}ValidatorTests.cs`);

    validatePathSecurity(testPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(testPath));
      await writeText(testPath, content);
    }

    result.files.push({
      path: path.relative(structure.root, testPath),
      content,
      type: 'created',
    });
  }

  result.instructions.push(`Add package reference: <PackageReference Include="FluentValidation.TestHelper" Version="11.*" />`);
}

async function scaffoldRepositoryTests(
  name: string,
  options: TestOptions,
  testTypes: string[],
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean
): Promise<void> {
  const testNamespace = `${config.conventions.namespaces.application}.Tests`;
  const infrastructureNamespace = config.conventions.namespaces.infrastructure;
  const domainNamespace = config.conventions.namespaces.domain;

  const context = {
    name,
    testNamespace,
    infrastructureNamespace,
    domainNamespace,
    ...options,
  };

  if (testTypes.includes('integration')) {
    const content = Handlebars.compile(repositoryTestTemplate)(context);
    const testPath = path.join(structure.root, 'Tests', 'Integration', 'Repositories', `${name}RepositoryTests.cs`);

    validatePathSecurity(testPath, structure.root);

    if (!dryRun) {
      await ensureDirectory(path.dirname(testPath));
      await writeText(testPath, content);
    }

    result.files.push({
      path: path.relative(structure.root, testPath),
      content,
      type: 'created',
    });
  }

  result.instructions.push(`Add package reference: <PackageReference Include="Microsoft.EntityFrameworkCore.InMemory" Version="8.*" />`);
}

// ============================================================================
// Formatting
// ============================================================================

function formatTestResult(
  result: ScaffoldResult,
  _target: string,
  name: string,
  dryRun: boolean
): string {
  const lines: string[] = [];

  lines.push(`# Scaffold Tests: ${name}`);
  lines.push('');

  if (dryRun) {
    lines.push('> **DRY RUN MODE** - No files were written');
    lines.push('');
  }

  if (!result.success) {
    lines.push('## Error');
    lines.push('');
    lines.push(result.instructions.join('\n'));
    return lines.join('\n');
  }

  lines.push(`## Generated Test Files (${result.files.length})`);
  lines.push('');

  for (const file of result.files) {
    lines.push(`### \`${file.path}\``);
    lines.push('');
    lines.push('```csharp');
    lines.push(file.content);
    lines.push('```');
    lines.push('');
  }

  if (result.instructions.length > 0) {
    lines.push('## Next Steps');
    lines.push('');
    for (const instruction of result.instructions) {
      lines.push(`- ${instruction}`);
    }
    lines.push('');
  }

  lines.push('## Test Conventions Applied');
  lines.push('');
  lines.push('- **Naming**: `{MethodName}_When{Condition}_Should{ExpectedResult}`');
  lines.push('- **Pattern**: AAA (Arrange-Act-Assert)');
  lines.push('- **Assertions**: FluentAssertions (`.Should()`)');
  lines.push('- **Mocking**: Moq (`Mock<T>`)');
  lines.push('');

  return lines.join('\n');
}
